import { Injectable, signal, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { map, switchMap, tap, catchError, Observable, of, throwError, finalize, timeout } from 'rxjs';
import { EnvironmentService } from '@app/core/services/environment.service';
import { ApiResponse } from '@app/features/credits/types/credits.interface';
import { SessionManager } from '@core/session/session-manager.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { IntercomService } from '@app/core/infrastructure/third-party';
import { AuthApiService } from './auth-api.service';
import { LoginResponse, LoginResult, TwoFactorSetupResponse } from '../types';
import { User } from '@app/core/types';

const CURRENT_USER_KEY = 'currentUser';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function isTwoFactorSetup(response: any): response is TwoFactorSetupResponse {
  return response && typeof response === 'object' && 'secretKey' in response;
}

@Injectable({ providedIn: 'root' })
export class AuthenticationService {
  private readonly intercomService = inject(IntercomService);
  private readonly router = inject(Router);
  private readonly http = inject(HttpClient);
  private readonly snackBar = inject(MatSnackBar);
  private readonly authApiService = inject(AuthApiService);
  private readonly environmentService = inject(EnvironmentService);
  private readonly sessionManager = inject(SessionManager);
  private readonly baseUrl = `${this.environmentService.apiBaseUrl}/Auth`;
  private readonly _currentUserSignal = signal<User | null>(this._getStoredUser());
  public readonly currentUser = this._currentUserSignal.asReadonly();
  private readonly twoFactorSetupData = signal<TwoFactorSetupResponse | null>(null);

  init(): Observable<User | null> {
    return this._validateSessionWithBackend().pipe(
      switchMap((response) => this._handleSuccessfulLogin(response)),
      catchError(() => {
        this.logoutSilently();
        return of(null);
      })
    );
  }

 login(companyNumber: number, userName:string, password: string):Observable<LoginResult> {
    const loginRequest$ = this.authApiService.login({ companyNumber, userName, password });

    return loginRequest$.pipe(
      switchMap((response: unknown): Observable<LoginResult> => {
        if (isTwoFactorSetup(response)) {
          this.twoFactorSetupData.set(response);
          return of({ status: 'TwoFactorRequired' });
        }

        const loginResponse = response as LoginResponse;

        if (loginResponse.success) {
          return this._handleSuccessfulLogin(loginResponse).pipe(
            map(user => {
              if (!user) {
                throw new Error('No se pudo procesar el usuario después del login.');
              }
              return { status: 'Success', user };
            })
          );
        }

        return throwError(() => new Error('Credenciales inválidas o respuesta inesperada.'));
      }),
      catchError(error => {
        this.logoutSilently();
        return throwError(() => error);
      })
    );
  }

  logout(): Observable<ApiResponse> {
    return this.authApiService.logout().pipe(
      finalize(() => {
        this._clearUserSession();
        this.router.navigate(['/auth/login']);
      })
    );
  }

  isLoggedIn(): boolean {
    return !!this.currentUser();
  }

  refreshUserData(): Observable<User | null> {
    return this.fetchUserInfo();
  }

  private _handleSuccessfulLogin(response: LoginResponse): Observable<User | null> {
    if (response.success) {
      if (response.cookieBind) {
        this.sessionManager.setSession(response.cookieBind, 7);
      }

      return this.fetchUserInfo().pipe(
        tap(user => {
          if (user && this.environmentService.isProduction) {
            this.intercomService.boot();
          }
        }),
        switchMap(user => {
          if (!user) {
            return throwError(() => new Error('No se pudo obtener la información del usuario.'));
          }

          return this.authApiService.updateConfiguration(true).pipe(
            timeout(5000),
            map(() => user),
            catchError(() => {
              return of(user);
            })
          );
        }),
        catchError(error => {
          return throwError(() => error);
        })
      );
    }

    return of(null);
  }

  private _validateSessionWithBackend(): Observable<LoginResponse> {
    return this.authApiService.getStatus().pipe(
      catchError(() => of({ success: false, cookieBind: '', isTwoFactorEnabled: false }))
    );
  }

  private fetchUserInfo(): Observable<User | null> {
    const userInfo$: Observable<User> = this.authApiService.getUserInfo().pipe(
      map(userInfo => ({
        serviceToken: this.sessionManager.getSession(),
        user: userInfo,
      }))
    );

    return userInfo$.pipe(
      tap(user => {
        if (user) {
          this._updateUserData(user);
        }
      }),
      catchError(() => {
        this.logoutSilently();
        return of(null);
      })
    );
  }

  private logoutSilently(): void {
    this._clearUserSession();
  }

  private _updateUserData(user: User): void {
    localStorage.setItem(CURRENT_USER_KEY, JSON.stringify(user));
    this._currentUserSignal.set(user);
  }

  private _clearUserSession(): void {
    localStorage.removeItem(CURRENT_USER_KEY);
    this.sessionManager.clearSession();
    this._currentUserSignal.set(null);
    this.intercomService.shutdown();
  }

  private _getStoredUser(): User | null {
    const storedUser = localStorage.getItem(CURRENT_USER_KEY);
    try {
      return storedUser ? (JSON.parse(storedUser) as User) : null;
    } catch (error) {
      console.error('Error al parsear el usuario almacenado:', error);
      localStorage.removeItem(CURRENT_USER_KEY);
      return null;
    }
  }

  configuration(activeNewVersion: boolean): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(`${this.baseUrl}/configuration`, { activeNewVersion });
  }

  sendResetPasswordEmail(email: string, isNewUrl: boolean): Observable<ApiResponse> {
    return this.authApiService.sendResetPasswordEmail(email, isNewUrl);
  }

  verifyResetPasswordToken(id: string, companyNumber: number): Observable<ApiResponse> {
    return this.authApiService.verifyResetPasswordToken(id, companyNumber);
  }

  twoFactorVerify(code: number): Observable<ApiResponse> {
    const setupData = this.twoFactorSetupData();

    if (!setupData) {
      this.router.navigate(['/auth/login']);
      this.snackBar.open('No se ha encontrado información del usuario. Por favor, inicie sesión de nuevo.', 'Cerrar', {
        duration: 7000,
        verticalPosition: 'top',
        horizontalPosition: 'center',
        panelClass: ['snack-bar-error']
      });
      return of({ success: false, message: 'Flujo de 2FA inválido, datos de configuración no encontrados.' });
    }

    const payload: TwoFactorSetupResponse = {
      ...setupData,
      code: code
    };

    return this.authApiService.verifyTwoFactorCode(payload).pipe(
     switchMap(response => {
      if (response.success) {
        this.twoFactorSetupData.set(null);
        return of(response);
      }
      return throwError(() => new Error(response.message || 'Código de verificación incorrecto'));
    }),
      catchError(err => throwError(() => err))
    );
  }

  resetPassword(id: string, companyNumber: number, newPassword: string): Observable<ApiResponse> {
    return this.authApiService.resetPassword({ id, companyNumber, newPassword });
  }
}
